// Emergency Cancellations Tab for Provider Dashboard
import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Clock,
  Calendar,
  User,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronRight,
  Phone,
  Mail,
  X,
  Archive,
  RotateCcw
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import FirebaseEmergencyCancellationService from '../../../services/firebase/FirebaseEmergencyCancellationService';
import FirebaseAppointmentService from '../../../services/firebase/FirebaseAppointmentService';
import {
  EmergencyCancellationRequest,
  CancellationStats,
  CANCELLATION_REASON_LABELS,
  URGENCY_CONFIG,
  STATUS_CONFIG
} from '../../../types/emergencyCancellation';
import VoiceInputButton from '../../VoiceInputButton';

const EmergencyCancellationsTab: React.FC = () => {
  const { user, userProfile } = useAuth();
  const providerId = user?.uid || '';
  const providerName = userProfile?.displayName || 'Provider';

  const [requests, setRequests] = useState<EmergencyCancellationRequest[]>([]);
  const [stats, setStats] = useState<CancellationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'pending' | 'all' | 'archived'>('pending');
  const [selectedRequest, setSelectedRequest] = useState<EmergencyCancellationRequest | null>(null);
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [processingNotes, setProcessingNotes] = useState('');
  const [denialReason, setDenialReason] = useState('');

  const cancellationService = FirebaseEmergencyCancellationService.getInstance();

  // Subscribe to requests
  useEffect(() => {
    if (!providerId) return;

    setLoading(true);

    if (filter === 'pending') {
      const unsubscribe = cancellationService.subscribeToPendingRequests(providerId, (reqs) => {
        setRequests(reqs);
        setLoading(false);
      });
      return () => unsubscribe();
    } else {
      // Both 'all' and 'archived' use the full list, filtered client-side
      const unsubscribe = cancellationService.subscribeToAllRequests(providerId, (reqs) => {
        if (filter === 'archived') {
          setRequests(reqs.filter(r => r.archived));
        } else {
          // 'all' — show non-archived only
          setRequests(reqs.filter(r => !r.archived));
        }
        setLoading(false);
      });
      return () => unsubscribe();
    }
  }, [providerId, filter]);

  // Load stats - also subscribe to all requests for accurate stats
  useEffect(() => {
    if (!providerId) return;

    const unsubscribeAll = cancellationService.subscribeToAllRequests(providerId, (allReqs) => {
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const thisMonth = allReqs.filter(r => r.requestedAt >= startOfMonth);
      const pending = allReqs.filter(r => r.status === 'pending' || r.status === 'acknowledged');
      const rescheduled = thisMonth.filter(r => r.status === 'rescheduled');

      // Average response time for processed requests
      const processed = thisMonth.filter(r => r.processedAt && r.requestedAt);
      let avgResponse = 0;
      if (processed.length > 0) {
        const total = processed.reduce((sum, r) => {
          return sum + (r.processedAt!.getTime() - r.requestedAt.getTime());
        }, 0);
        avgResponse = Math.round(total / processed.length / 60000);
      }

      setStats({
        totalThisMonth: thisMonth.length,
        pendingRequests: pending.length,
        byReason: {},
        rescheduledCount: rescheduled.length,
        averageResponseTime: avgResponse
      });
    });

    return () => unsubscribeAll();
  }, [providerId]);

  // Format timestamp
  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Handle acknowledge (for reschedule requests)
  const handleAcknowledge = async (request: EmergencyCancellationRequest) => {
    setProcessingAction('acknowledge');
    try {
      await cancellationService.acknowledgeRequest(request.id, providerId, providerName);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error acknowledging request:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  // Handle dismiss (for cancel-only requests — appointment already cancelled)
  const handleDismiss = async (request: EmergencyCancellationRequest) => {
    setProcessingAction('dismiss');
    try {
      await cancellationService.approveRequest(request.id, providerName, 'Dismissed — cancellation already processed');
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error dismissing request:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  // Handle approve
  const handleApprove = async () => {
    if (!selectedRequest) return;
    setProcessingAction('approve');
    try {
      await cancellationService.approveRequest(
        selectedRequest.id,
        providerName,
        processingNotes || undefined
      );
      setSelectedRequest(null);
      setProcessingNotes('');
    } catch (error) {
      console.error('Error approving request:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  // Handle reschedule — creates a real appointment then updates the cancellation request
  const handleReschedule = async () => {
    if (!selectedRequest || !rescheduleDate || !rescheduleTime) return;
    setProcessingAction('reschedule');
    try {
      const appointmentService = FirebaseAppointmentService.getInstance();

      // Calculate end time (default 50-min follow-up)
      const [hours, minutes] = rescheduleTime.split(':').map(Number);
      const endDate = new Date(2000, 0, 1, hours, minutes + 50);
      const endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;

      // Create real appointment in the appointments collection
      const newAppointment = await appointmentService.createAppointment({
        patientId: selectedRequest.clientId,
        patientName: selectedRequest.clientName,
        providerId: selectedRequest.providerId,
        providerName: selectedRequest.providerName,
        appointmentDate: new Date(rescheduleDate),
        startTime: rescheduleTime,
        endTime,
        duration: 50,
        modality: selectedRequest.appointmentType === 'video' ? 'video' : 'in-person',
        sessionType: 'follow-up',
        status: 'scheduled',
        createdBy: providerId,
        appointmentReason: `Rescheduled from emergency cancellation on ${selectedRequest.appointmentDate}`,
      });

      // Update the cancellation request with the real appointment ID
      await cancellationService.rescheduleFromRequest(
        selectedRequest.id,
        providerName,
        {
          appointmentId: newAppointment.id,
          date: rescheduleDate,
          time: rescheduleTime
        },
        processingNotes || undefined
      );
      setSelectedRequest(null);
      setRescheduleDate('');
      setRescheduleTime('');
      setProcessingNotes('');
    } catch (error) {
      console.error('Error rescheduling:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  // Handle archive
  const handleArchive = async (request: EmergencyCancellationRequest) => {
    setProcessingAction('archive');
    try {
      await cancellationService.archiveRequest(request.id);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error archiving request:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  // Handle unarchive
  const handleUnarchive = async (request: EmergencyCancellationRequest) => {
    setProcessingAction('unarchive');
    try {
      await cancellationService.unarchiveRequest(request.id);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error unarchiving request:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  // Handle deny
  const handleDeny = async () => {
    if (!selectedRequest || !denialReason) return;
    setProcessingAction('deny');
    try {
      await cancellationService.denyRequest(
        selectedRequest.id,
        providerName,
        denialReason
      );
      setSelectedRequest(null);
      setDenialReason('');
    } catch (error) {
      console.error('Error denying request:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
        <span style={styles.loadingText}>Loading cancellation requests...</span>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <AlertTriangle size={24} color="#dc3545" />
          <div>
            <h1 style={styles.title}>Emergency Cancellations</h1>
            <p style={styles.subtitle}>
              Manage urgent appointment cancellation requests
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <span style={styles.statValue}>{stats.pendingRequests}</span>
            <span style={styles.statLabel}>Pending</span>
          </div>
          <div style={styles.statCard}>
            <span style={styles.statValue}>{stats.totalThisMonth}</span>
            <span style={styles.statLabel}>This Month</span>
          </div>
          <div style={styles.statCard}>
            <span style={styles.statValue}>{stats.rescheduledCount}</span>
            <span style={styles.statLabel}>Rescheduled</span>
          </div>
          <div style={styles.statCard}>
            <span style={styles.statValue}>
              {stats.averageResponseTime > 0
                ? stats.averageResponseTime >= 60
                  ? `${Math.round(stats.averageResponseTime / 60)}h`
                  : `${stats.averageResponseTime}m`
                : '-'}
            </span>
            <span style={styles.statLabel}>Avg Response</span>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div style={styles.filterTabs}>
        <button
          onClick={() => setFilter('pending')}
          style={{
            ...styles.filterTab,
            ...(filter === 'pending' ? styles.filterTabActive : {})
          }}
        >
          Pending ({requests.filter(r => r.status === 'pending' || r.status === 'acknowledged').length})
        </button>
        <button
          onClick={() => setFilter('all')}
          style={{
            ...styles.filterTab,
            ...(filter === 'all' ? styles.filterTabActive : {})
          }}
        >
          All Requests
        </button>
        <button
          onClick={() => setFilter('archived')}
          style={{
            ...styles.filterTab,
            ...(filter === 'archived' ? styles.filterTabActive : {})
          }}
        >
          <Archive size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
          Archived
        </button>
      </div>

      {/* Requests List */}
      {requests.length === 0 ? (
        <div style={styles.emptyState}>
          <CheckCircle size={48} color="#22c55e" />
          <h3 style={styles.emptyTitle}>No pending requests</h3>
          <p style={styles.emptyText}>
            {filter === 'pending'
              ? 'All emergency cancellation requests have been processed.'
              : filter === 'archived'
              ? 'No archived requests.'
              : 'No emergency cancellation requests found.'}
          </p>
        </div>
      ) : (
        <div style={styles.requestsList}>
          {requests.map((request) => {
            const urgencyConfig = URGENCY_CONFIG[request.urgency];
            const statusConfig = STATUS_CONFIG[request.status];

            return (
              <div
                key={request.id}
                onClick={() => setSelectedRequest(request)}
                style={{
                  ...styles.requestCard,
                  borderLeftColor: urgencyConfig.color
                }}
              >
                <div style={styles.requestHeader}>
                  <div style={styles.clientInfo}>
                    <span style={styles.clientName}>{request.clientName}</span>
                    <span
                      style={{
                        ...styles.urgencyBadge,
                        backgroundColor: urgencyConfig.bgColor,
                        color: urgencyConfig.color
                      }}
                    >
                      {urgencyConfig.label}
                    </span>
                    <span
                      style={{
                        ...styles.statusBadge,
                        backgroundColor: statusConfig.bgColor,
                        color: statusConfig.color
                      }}
                    >
                      {statusConfig.label}
                    </span>
                  </div>
                  <span style={styles.timestamp}>
                    {formatTimestamp(request.requestedAt)}
                  </span>
                </div>

                <div style={styles.appointmentInfo}>
                  <Calendar size={14} />
                  <span>{request.appointmentDate} at {request.appointmentTime}</span>
                  <span style={styles.appointmentType}>
                    ({request.appointmentType})
                  </span>
                </div>

                <div style={styles.reasonInfo}>
                  <span style={styles.reasonCategory}>
                    {CANCELLATION_REASON_LABELS[request.reasonCategory]}
                  </span>
                  <span style={styles.reasonPreview}>
                    {request.reasonDetails.substring(0, 100)}
                    {request.reasonDetails.length > 100 ? '...' : ''}
                  </span>
                </div>

                {request.wantsToReschedule && (
                  <div style={styles.rescheduleNote}>
                    <RefreshCw size={14} />
                    <span>Wants to reschedule</span>
                  </div>
                )}

                <ChevronRight
                  size={20}
                  color="#64748b"
                  style={styles.chevron}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Request Detail Modal */}
      {selectedRequest && (
        <>
          <div
            style={styles.modalBackdrop}
            onClick={() => setSelectedRequest(null)}
          />
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Cancellation Request</h2>
              <button
                onClick={() => setSelectedRequest(null)}
                style={styles.modalClose}
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>

            <div style={styles.modalBody}>
              {/* Client Info */}
              <div style={styles.modalSection}>
                <h4 style={styles.sectionLabel}>Client</h4>
                <div style={styles.clientDetails}>
                  <User size={16} />
                  <span>{selectedRequest.clientName}</span>
                </div>
                {selectedRequest.clientPhone && (
                  <div style={styles.contactItem}>
                    <Phone size={14} />
                    <a href={`tel:${selectedRequest.clientPhone}`}>
                      {selectedRequest.clientPhone}
                    </a>
                  </div>
                )}
                {selectedRequest.clientEmail && (
                  <div style={styles.contactItem}>
                    <Mail size={14} />
                    <a href={`mailto:${selectedRequest.clientEmail}`}>
                      {selectedRequest.clientEmail}
                    </a>
                  </div>
                )}
              </div>

              {/* Appointment */}
              <div style={styles.modalSection}>
                <h4 style={styles.sectionLabel}>Appointment</h4>
                <div style={styles.appointmentDetail}>
                  <Calendar size={16} />
                  <span>{selectedRequest.appointmentDate} at {selectedRequest.appointmentTime}</span>
                </div>
                <span style={styles.appointmentTypeDetail}>
                  {selectedRequest.appointmentType === 'video' ? 'Video Session' : 'In-Person'}
                </span>
              </div>

              {/* Urgency & Reason */}
              <div style={styles.modalSection}>
                <h4 style={styles.sectionLabel}>Urgency</h4>
                <span
                  style={{
                    ...styles.urgencyBadgeLarge,
                    backgroundColor: URGENCY_CONFIG[selectedRequest.urgency].bgColor,
                    color: URGENCY_CONFIG[selectedRequest.urgency].color
                  }}
                >
                  {URGENCY_CONFIG[selectedRequest.urgency].label}
                </span>
              </div>

              <div style={styles.modalSection}>
                <h4 style={styles.sectionLabel}>Reason</h4>
                <span style={styles.reasonCategoryLarge}>
                  {CANCELLATION_REASON_LABELS[selectedRequest.reasonCategory]}
                </span>
                <p style={styles.reasonDetailsText}>
                  {selectedRequest.reasonDetails}
                </p>
              </div>

              {/* Reschedule Preference */}
              {selectedRequest.wantsToReschedule && (
                <div style={styles.modalSection}>
                  <h4 style={styles.sectionLabel}>Reschedule Preference</h4>
                  {selectedRequest.preferredRescheduleDate && (
                    <div style={styles.preferenceItem}>
                      <strong>Preferred Date:</strong> {selectedRequest.preferredRescheduleDate}
                    </div>
                  )}
                  {selectedRequest.preferredRescheduleTime && (
                    <div style={styles.preferenceItem}>
                      <strong>Preferred Time:</strong> {selectedRequest.preferredRescheduleTime}
                    </div>
                  )}
                  {selectedRequest.rescheduleNotes && (
                    <div style={styles.preferenceItem}>
                      <strong>Notes:</strong> {selectedRequest.rescheduleNotes}
                    </div>
                  )}
                </div>
              )}

              {/* Actions for Pending/Acknowledged */}
              {(selectedRequest.status === 'pending' || selectedRequest.status === 'acknowledged') && (
                <div style={styles.actionsSection}>
                  {selectedRequest.status === 'pending' && !selectedRequest.wantsToReschedule && (
                    /* Cancel-only: just show Dismiss — appointment already cancelled */
                    <div style={styles.actionGroup}>
                      <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#94a3b8' }}>
                        This cancellation has already been processed. The appointment was cancelled automatically.
                      </p>
                      <button
                        onClick={() => handleDismiss(selectedRequest)}
                        disabled={processingAction !== null}
                        style={styles.approveButton}
                      >
                        <CheckCircle size={16} />
                        {processingAction === 'dismiss' ? 'Processing...' : 'Dismiss'}
                      </button>
                    </div>
                  )}

                  {selectedRequest.status === 'pending' && selectedRequest.wantsToReschedule && (
                    /* Reschedule request: acknowledge to review */
                    <button
                      onClick={() => handleAcknowledge(selectedRequest)}
                      disabled={processingAction !== null}
                      style={styles.acknowledgeButton}
                    >
                      {processingAction === 'acknowledge' ? 'Processing...' : 'Acknowledge & Review Reschedule'}
                    </button>
                  )}

                  {selectedRequest.status === 'acknowledged' && (
                    <>
                      {/* Reschedule Section (primary action for reschedule requests) */}
                      {selectedRequest.wantsToReschedule && (
                        <div style={styles.actionGroup}>
                          <h4 style={styles.actionGroupTitle}>Approve Reschedule</h4>
                          <div style={styles.rescheduleInputs}>
                            <input
                              type="date"
                              value={rescheduleDate}
                              onChange={(e) => setRescheduleDate(e.target.value)}
                              style={styles.rescheduleInput}
                              min={new Date().toISOString().split('T')[0]}
                            />
                            <input
                              type="time"
                              value={rescheduleTime}
                              onChange={(e) => setRescheduleTime(e.target.value)}
                              style={styles.rescheduleInput}
                            />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <label style={{ fontSize: '13px', color: '#94a3b8' }}>Notes</label>
                            <VoiceInputButton onResult={text => setProcessingNotes(prev => prev + ' ' + text)} />
                          </div>
                          <textarea
                            value={processingNotes}
                            onChange={(e) => setProcessingNotes(e.target.value)}
                            placeholder="Add notes (optional)..."
                            style={styles.actionTextarea}
                            rows={2}
                          />
                          <button
                            onClick={handleReschedule}
                            disabled={processingAction !== null || !rescheduleDate || !rescheduleTime}
                            style={{
                              ...styles.rescheduleButton,
                              ...(!rescheduleDate || !rescheduleTime ? styles.buttonDisabled : {})
                            }}
                          >
                            <RefreshCw size={16} />
                            {processingAction === 'reschedule' ? 'Processing...' : 'Approve Reschedule'}
                          </button>
                        </div>
                      )}

                      {/* Approve Cancellation (for acknowledged reschedule requests that provider wants to just approve cancel) */}
                      <div style={styles.actionGroup}>
                        <h4 style={styles.actionGroupTitle}>
                          {selectedRequest.wantsToReschedule ? 'Or Just Approve Cancellation' : 'Approve Cancellation'}
                        </h4>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <label style={{ fontSize: '13px', color: '#94a3b8' }}>Notes</label>
                          <VoiceInputButton onResult={text => setProcessingNotes(prev => prev + ' ' + text)} />
                        </div>
                        <textarea
                          value={processingNotes}
                          onChange={(e) => setProcessingNotes(e.target.value)}
                          placeholder="Add notes (optional)..."
                          style={styles.actionTextarea}
                          rows={2}
                        />
                        <button
                          onClick={handleApprove}
                          disabled={processingAction !== null}
                          style={styles.approveButton}
                        >
                          <CheckCircle size={16} />
                          {processingAction === 'approve' ? 'Processing...' : 'Approve Cancellation'}
                        </button>
                      </div>

                      {/* Deny Section */}
                      <div style={styles.actionGroup}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <h4 style={{ ...styles.actionGroupTitle, margin: 0 }}>Deny Request</h4>
                          <VoiceInputButton onResult={text => setDenialReason(prev => prev + ' ' + text)} />
                        </div>
                        <textarea
                          value={denialReason}
                          onChange={(e) => setDenialReason(e.target.value)}
                          placeholder="Reason for denial (required)..."
                          style={styles.actionTextarea}
                          rows={2}
                        />
                        <button
                          onClick={handleDeny}
                          disabled={processingAction !== null || !denialReason}
                          style={{
                            ...styles.denyButton,
                            ...(!denialReason ? styles.buttonDisabled : {})
                          }}
                        >
                          <XCircle size={16} />
                          {processingAction === 'deny' ? 'Processing...' : 'Deny Request'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Processed Info */}
              {selectedRequest.processedAt && (
                <div style={styles.processedInfo}>
                  <h4 style={styles.sectionLabel}>Processed</h4>
                  <p>
                    {STATUS_CONFIG[selectedRequest.status].label} by {selectedRequest.processedBy} on{' '}
                    {selectedRequest.processedAt.toLocaleDateString()}
                  </p>
                  {selectedRequest.processingNotes && (
                    <p><strong>Notes:</strong> {selectedRequest.processingNotes}</p>
                  )}
                  {selectedRequest.newAppointmentDate && (
                    <p>
                      <strong>New Appointment:</strong> {selectedRequest.newAppointmentDate} at {selectedRequest.newAppointmentTime}
                    </p>
                  )}
                  {selectedRequest.denialReason && (
                    <p><strong>Denial Reason:</strong> {selectedRequest.denialReason}</p>
                  )}
                </div>
              )}

              {/* Archive / Unarchive button for resolved requests */}
              {['approved', 'rescheduled', 'denied'].includes(selectedRequest.status) && (
                <div style={{ marginTop: '16px' }}>
                  {selectedRequest.archived ? (
                    <button
                      onClick={() => handleUnarchive(selectedRequest)}
                      disabled={processingAction !== null}
                      style={styles.unarchiveButton}
                    >
                      <RotateCcw size={16} />
                      {processingAction === 'unarchive' ? 'Restoring...' : 'Restore from Archive'}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleArchive(selectedRequest)}
                      disabled={processingAction !== null}
                      style={styles.archiveButton}
                    >
                      <Archive size={16} />
                      {processingAction === 'archive' ? 'Archiving...' : 'Archive Request'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '24px',
    backgroundColor: '#0f172a',
    minHeight: '100%'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    gap: '16px'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #334155',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  loadingText: {
    color: '#64748b',
    fontSize: '14px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '600',
    color: '#f8fafc'
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    color: '#64748b'
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '12px',
    marginBottom: '24px'
  },
  statCard: {
    padding: '16px',
    backgroundColor: '#1e293b',
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  statValue: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#f8fafc'
  },
  statLabel: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '4px'
  },
  filterTabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px'
  },
  filterTab: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: '1px solid #334155',
    backgroundColor: 'transparent',
    color: '#94a3b8',
    fontSize: '14px',
    cursor: 'pointer'
  },
  filterTabActive: {
    backgroundColor: '#3b82f6',
    borderColor: '#3b82f6',
    color: '#fff'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center'
  },
  emptyTitle: {
    margin: '16px 0 8px 0',
    fontSize: '18px',
    fontWeight: '600',
    color: '#f8fafc'
  },
  emptyText: {
    margin: 0,
    fontSize: '14px',
    color: '#64748b'
  },
  requestsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  requestCard: {
    padding: '16px 20px',
    backgroundColor: '#1e293b',
    borderRadius: '10px',
    borderLeft: '4px solid',
    cursor: 'pointer',
    position: 'relative',
    transition: 'transform 0.2s'
  },
  requestHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  clientInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  clientName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#f8fafc'
  },
  urgencyBadge: {
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600'
  },
  statusBadge: {
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600'
  },
  timestamp: {
    fontSize: '12px',
    color: '#64748b'
  },
  appointmentInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#94a3b8',
    marginBottom: '8px'
  },
  appointmentType: {
    color: '#64748b'
  },
  reasonInfo: {
    marginBottom: '8px'
  },
  reasonCategory: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#f8fafc',
    marginRight: '8px'
  },
  reasonPreview: {
    fontSize: '13px',
    color: '#64748b'
  },
  rescheduleNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#3b82f6'
  },
  chevron: {
    position: 'absolute',
    right: '16px',
    top: '50%',
    transform: 'translateY(-50%)'
  },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 1000
  },
  modal: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    backgroundColor: '#0f172a',
    borderRadius: '16px',
    border: '1px solid #334155',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1001
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: '1px solid #334155'
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#f8fafc'
  },
  modalClose: {
    background: 'none',
    border: 'none',
    padding: '8px',
    cursor: 'pointer',
    color: '#64748b'
  },
  modalBody: {
    flex: 1,
    overflow: 'auto',
    padding: '20px 24px'
  },
  modalSection: {
    marginBottom: '20px'
  },
  sectionLabel: {
    margin: '0 0 8px 0',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  clientDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '15px',
    color: '#f8fafc',
    marginBottom: '8px'
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#3b82f6',
    marginBottom: '4px'
  },
  appointmentDetail: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '15px',
    color: '#f8fafc'
  },
  appointmentTypeDetail: {
    fontSize: '13px',
    color: '#64748b',
    marginTop: '4px'
  },
  urgencyBadgeLarge: {
    display: 'inline-block',
    padding: '6px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600'
  },
  reasonCategoryLarge: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#f8fafc',
    marginBottom: '8px'
  },
  reasonDetailsText: {
    margin: 0,
    fontSize: '14px',
    color: '#94a3b8',
    lineHeight: '1.5'
  },
  preferenceItem: {
    fontSize: '14px',
    color: '#94a3b8',
    marginBottom: '4px'
  },
  actionsSection: {
    borderTop: '1px solid #334155',
    paddingTop: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  actionGroup: {
    padding: '16px',
    backgroundColor: '#1e293b',
    borderRadius: '10px'
  },
  actionGroupTitle: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    fontWeight: '500',
    color: '#f8fafc'
  },
  actionTextarea: {
    width: '100%',
    padding: '10px',
    borderRadius: '8px',
    border: '1px solid #334155',
    backgroundColor: '#0f172a',
    color: '#f8fafc',
    fontSize: '14px',
    resize: 'none',
    fontFamily: 'inherit',
    marginBottom: '12px'
  },
  rescheduleInputs: {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px'
  },
  rescheduleInput: {
    flex: 1,
    padding: '10px',
    borderRadius: '8px',
    border: '1px solid #334155',
    backgroundColor: '#0f172a',
    color: '#f8fafc',
    fontSize: '14px'
  },
  acknowledgeButton: {
    padding: '12px 20px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#17a2b8',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  approveButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#22c55e',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  rescheduleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#6f42c1',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  denyButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#dc3545',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  processedInfo: {
    padding: '16px',
    backgroundColor: '#1e293b',
    borderRadius: '10px',
    marginTop: '16px'
  },
  archiveButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #334155',
    backgroundColor: 'transparent',
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  unarchiveButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #334155',
    backgroundColor: 'transparent',
    color: '#3b82f6',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  }
};

export default EmergencyCancellationsTab;
